import React, { createContext, useContext, useState, useCallback } from 'react';
import type { ShoppingCartData } from '../components/webdesigned/types/shoppingCart';
import type { CartItem } from '../services/cartService';
import { addToCart, getCartItems } from '../services/cartService';

/** VUM promo code details stored after client redeems a validated code */
export type VumPromo = {
  /** Convex document ID of the vumCodes row — used to mark it as booked after reservation */
  vumCodeId: string;
  code: string;
  guestFirstName: string;
  guestLastName: string;
  packageName: string;
  optionLabel: string;
  /** Number of nights included in the forfait (at VUM price — no extra charge) */
  nights: number;
  /** Amount the client paid to VUM for this forfait */
  forfaitPrice: number;
  maxPersons: number;
  minPersons?: number;
  maxAdults?: number;
  maxChildren?: number;
  /** Allowed accommodation types (e.g. ['chalet']). Empty = any. */
  accommodationTypes: string[];
  /** If set, only this specific accommodation is allowed (legacy single select) */
  specificAccommodationId?: string;
  /** If set, only these accommodations are allowed (multi-select) */
  specificAccommodationIds?: string[];
  includedActivities: Array<{ label: string; quantity?: number; valueUpTo?: number }>;
};

export type CheckoutState = {
  isOpen: boolean;
  selectedItems: CartItem[];
  shoppingCartData: ShoppingCartData | null;
  paymentMode: 'guest' | 'member';
};

export type ReservationFormState = {
  isOpen: boolean;
  itemType: 'accommodation' | 'activity' | 'package';
  itemId: string;
  itemName: string;
  itemPrice: number;
  shoppingCartData: ShoppingCartData | null;
  paymentMode?: 'guest' | 'member';
  /** Per accommodation stay setting; omitted means apply taxe d'hébergement (backward compat) */
  taxeHebergementEnabled?: boolean;
};

export type OpenReservationFormOptions = {
  paymentMode?: 'guest' | 'member';
  taxeHebergementEnabled?: boolean;
};

const defaultReservationForm: ReservationFormState = {
  isOpen: false,
  itemType: 'accommodation',
  itemId: '',
  itemName: '',
  itemPrice: 0,
  shoppingCartData: null,
};

export type WebDesignedCartContextValue = {
  assistantOpen: boolean;
  setAssistantOpen: (v: boolean) => void;
  toggleAssistant: () => void;
  assistantMinimized: boolean;
  setAssistantMinimized: (v: boolean) => void;
  cartSidebarOpen: boolean;
  setCartSidebarOpen: (v: boolean) => void;
  shoppingCartData: ShoppingCartData | null;
  setShoppingCartData: (data: ShoppingCartData | null) => void;
  checkout: CheckoutState;
  openCheckout: (selectedItems: CartItem[], shoppingCartData: ShoppingCartData | null, paymentMode?: 'guest' | 'member') => void;
  closeCheckout: () => void;
  /** Auth panel (slide-from-right, like landing): open for login/signup without navigating away */
  authPanelOpen: boolean;
  authPanelMode: 'signin' | 'signup';
  setAuthPanelOpen: (v: boolean) => void;
  setAuthPanelMode: (mode: 'signin' | 'signup') => void;
  openAuthPanel: (mode?: 'signin' | 'signup') => void;
  /** Reservation form (Quick Reservation panel, like landing) */
  reservationForm: ReservationFormState;
  openReservationForm: (
    itemType: 'accommodation' | 'activity' | 'package',
    itemId: string,
    itemName: string,
    itemPrice: number,
    options?: OpenReservationFormOptions,
  ) => void;
  closeReservationForm: () => void;
  /** VUM promo code applied by the client */
  vumPromo: VumPromo | null;
  setVumPromo: (promo: VumPromo | null) => void;
  /** Open the VUM promo code entry modal */
  vumPromoModalOpen: boolean;
  setVumPromoModalOpen: (v: boolean) => void;
  /**
   * Expand the booking calendar assistant (left panel).
   * Pass `{ returnToCart: true }` to reopen the cart when the assistant closes (mobile travel-info path).
   */
  openBookingAssistant: (options?: { returnToCart?: boolean }) => void;
};

const defaultCheckout: CheckoutState = {
  isOpen: false,
  selectedItems: [],
  shoppingCartData: null,
  paymentMode: 'guest',
};

const WebDesignedCartContext = createContext<WebDesignedCartContextValue | null>(null);

export function WebDesignedCartProvider({ children }: { children: React.ReactNode }) {
  /** Desktop PC: open by default for travel-info onboarding. Mobile/pad stay closed. */
  const [assistantOpen, setAssistantOpen] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth >= 1367 : false
  );
  const [assistantMinimized, setAssistantMinimized] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < 1367 : true
  );
  const [cartSidebarOpen, setCartSidebarOpen] = useState(false);
  const [shoppingCartData, setShoppingCartData] = useState<ShoppingCartData | null>(null);
  const [checkout, setCheckout] = useState<CheckoutState>(defaultCheckout);
  const [authPanelOpen, setAuthPanelOpen] = useState(false);
  const [authPanelMode, setAuthPanelMode] = useState<'signin' | 'signup'>('signin');
  const [reservationForm, setReservationForm] = useState<ReservationFormState>(defaultReservationForm);
  const [vumPromo, setVumPromo] = useState<VumPromo | null>(null);
  const [vumPromoModalOpen, setVumPromoModalOpen] = useState(false);
  const [returnToCartAfterAssistant, setReturnToCartAfterAssistant] = useState(false);

  const toggleAssistant = useCallback(() => setAssistantOpen((prev) => !prev), []);

  const setAssistantMinimizedWithReturn = useCallback((minimized: boolean) => {
    setAssistantMinimized(minimized);
    if (minimized && returnToCartAfterAssistant) {
      setReturnToCartAfterAssistant(false);
      setCartSidebarOpen(true);
    }
  }, [returnToCartAfterAssistant]);

  const setAssistantOpenWithReturn = useCallback((open: boolean) => {
    setAssistantOpen(open);
    if (!open && returnToCartAfterAssistant) {
      setReturnToCartAfterAssistant(false);
      setCartSidebarOpen(true);
    }
  }, [returnToCartAfterAssistant]);

  const openReservationForm = useCallback(
    (
      itemType: 'accommodation' | 'activity' | 'package',
      itemId: string,
      itemName: string,
      itemPrice: number,
      options?: OpenReservationFormOptions,
    ) => {
      const paymentMode = options?.paymentMode;
      const taxeHebergementEnabled = options?.taxeHebergementEnabled;
      // When a VUM promo with included activities is active and the user clicks "Réserver"
      // on an accommodation, skip the quick reservation form: add the accommodation to the
      // cart directly and open the cart sidebar instead.
      if (itemType === 'accommodation' && vumPromo && vumPromo.includedActivities.length > 0) {
        // Block adding a second accommodation when a VUM promo is active —
        // the forfait covers exactly ONE accommodation.
        const existingAccommodations = getCartItems().filter((i) => i.type === 'accommodation');
        if (existingAccommodations.length > 0) {
          alert('Votre forfait VUM couvre un seul hébergement. Retirez l\'hébergement actuel avant d\'en choisir un autre.');
          setCartSidebarOpen(true);
          return;
        }
        addToCart({
          type: 'accommodation',
          itemId,
          itemName,
          itemPrice,
          ...(taxeHebergementEnabled !== undefined ? { taxeHebergementEnabled } : {}),
        });
        setCartSidebarOpen(true);
        return;
      }

      setReservationForm({
        isOpen: true,
        itemType,
        itemId,
        itemName,
        itemPrice,
        shoppingCartData: shoppingCartData ?? null,
        paymentMode,
        ...(taxeHebergementEnabled !== undefined ? { taxeHebergementEnabled } : {}),
      });
    },
    [shoppingCartData, vumPromo]
  );

  const closeReservationForm = useCallback(() => {
    setReservationForm(defaultReservationForm);
  }, []);

  const openCheckout = useCallback(
    (selectedItems: CartItem[], data: ShoppingCartData | null, paymentMode: 'guest' | 'member' = 'guest') => {
      setCheckout({ isOpen: true, selectedItems, shoppingCartData: data, paymentMode });
    },
    []
  );

  const closeCheckout = useCallback(() => {
    setCheckout(defaultCheckout);
  }, []);

  const openAuthPanel = useCallback((mode: 'signin' | 'signup' = 'signin') => {
    setAuthPanelMode(mode);
    setAuthPanelOpen(true);
  }, []);

  const openBookingAssistant = useCallback((options?: { returnToCart?: boolean }) => {
    if (options?.returnToCart) setReturnToCartAfterAssistant(true);
    setAssistantOpen(true);
    setAssistantMinimized(false);
  }, []);

  const value: WebDesignedCartContextValue = {
    assistantOpen,
    setAssistantOpen: setAssistantOpenWithReturn,
    toggleAssistant,
    assistantMinimized,
    setAssistantMinimized: setAssistantMinimizedWithReturn,
    cartSidebarOpen,
    setCartSidebarOpen,
    shoppingCartData,
    setShoppingCartData,
    checkout,
    openCheckout,
    closeCheckout,
    authPanelOpen,
    authPanelMode,
    setAuthPanelOpen,
    setAuthPanelMode,
    openAuthPanel,
    reservationForm,
    openReservationForm,
    closeReservationForm,
    vumPromo,
    setVumPromo,
    vumPromoModalOpen,
    setVumPromoModalOpen,
    openBookingAssistant,
  };

  return (
    <WebDesignedCartContext.Provider value={value}>
      {children}
    </WebDesignedCartContext.Provider>
  );
}

export function useWebDesignedCart() {
  const ctx = useContext(WebDesignedCartContext);
  return ctx;
}
